import { useMemoizedContentGetter } from 'utils/contentful'
import { Box, Grid } from '@achieve/ascend'
import { get as _get } from 'lodash-es'
import { MediaImageStandard } from 'components/Contentful'
import Section from 'components/Section'
import { Carousel } from 'components/Carousels'
import styles from './MultiSlider.module.scss'
import { Eyebrow } from 'components/Eyebrow'
import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

// TODO - add schema validation
function MultiSlider({ content, classNameSection }) {
  const { eyebrow, sliderItems } = useMemoizedContentGetter(content, ['sliderItems', 'eyebrow'])
  const carouselItemsCollection = _get(sliderItems, 'carouselItemsCollection', [])
  const config = content?.fields?.additionalConfiguration?.jsonContent
  const isOne = carouselItemsCollection?.items?.length === 1
  const list =
    carouselItemsCollection?.items?.length > 1
      ? carouselItemsCollection?.items
      : carouselItemsCollection?.items?.[0]?.gridItemsCollection?.items

  const sliderRef = useRef(null)
  const trackRef = useRef(null)
  const [isScrollable, setIsScrollable] = useState(false)

  useEffect(() => {
    const checkScrollable = () => {
      if (sliderRef.current && trackRef.current) {
        setIsScrollable(trackRef.current.scrollWidth > window.innerWidth)
      }
    }
    checkScrollable()
    window.addEventListener('resize', checkScrollable)

    return () => {
      window.removeEventListener('resize', checkScrollable)
    }
  }, [sliderRef, trackRef])

  const SlideItem = ({ mediaAltText, mediaContent }) => {
    return (
      <Grid item className={styles['multi-slider-item']}>
        <span>
          <MediaImageStandard
            alt={mediaAltText}
            content={mediaContent}
            layout="fill"
            objectFit="contain"
            width="150"
            height="50"
            data-testid="multi-slider-item-image"
          />
        </span>
      </Grid>
    )
  }

  function Slide({ item }) {
    const slideChildItems = _get(item, 'gridItemsCollection.items', [])
    return isOne ? (
      <div className={styles['multi-slider-one']} data-testid="multi-slider-one">
        <SlideItem
          key={item?.fieldName}
          mediaAltText={item?.mediaAltText}
          mediaContent={item?.mediaContent}
        />
      </div>
    ) : (
      <Grid
        container
        className={styles['multi-slider-slide']}
        alignItems="center"
        data-testid="multi-slider"
      >
        {slideChildItems.map((item, index) => (
          <span key={`item-${index}`}>
            <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
          </span>
        ))}
      </Grid>
    )
  }

  function InfinityLogos() {
    return (
      <div className={styles['slider']} ref={sliderRef} data-testid="multi-slider-infinity-logos">
        <div
          className={classNames(styles['slider-track'], styles[`${isScrollable ? 'scroll' : ''}`])}
          ref={trackRef}
        >
          {list.map((item, index) => (
            <div
              className={styles['slide']}
              key={`item-${index}`}
              data-testid={`multi-slider-item-${index}`}
            >
              <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
            </div>
          ))}
          {isScrollable &&
            list.map((item, index) => (
              <div className={styles['slide']} key={`item-scroll-${index}`}>
                <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
              </div>
            ))}
        </div>
      </div>
    )
  }

  return (
    <Section
      childContainerClassName={styles['multi-slider-container']}
      className={classNames([
        styles['multi-slider-section'],
        classNameSection,
        {
          'py-0 md:py-[64px]': config?.condensedSection,
        },
      ])}
      data-testid="multi-slider-section"
      relative={false}
    >
      {eyebrow && (
        <Eyebrow
          content={eyebrow?.textContent}
          black={config?.eyebrowBlack}
          variant="bodySm"
          fontWeight="bold"
          component="h3"
          className={styles['multi-slider-eyebrow']}
        />
      )}
      <Box
        className={classNames(styles['multi-slider'], {
          'max-w-[425px] lg:max-w-max flex flex-col justify-self-center': config?.gridLogos,
        })}
        data-carousel-variant={config?.carouselVariant}
      >
        {config?.loopLogos ? (
          <div className={styles['multi-slider-loop']} data-testid="multi-slider-loop">
            <InfinityLogos />
          </div>
        ) : config?.gridLogos ? (
          <div
            className="flex flex-col md:flex-row gap-1 px-4 lg:px-[300px]"
            data-testid="multi-slider-grid"
          >
            {list?.map(({ mediaAltText, mediaContent }, index) => (
              <div
                key={`item-slide-${index}`}
                className={classNames('py-4 h-[130px] md:h-[100px]', {
                  'border-solid border-0 border-b-[1px] border-b-gray-300 md:border-b-0':
                    index < list.length - 1,
                })}
              >
                <MediaImageStandard
                  alt={mediaAltText}
                  content={mediaContent}
                  objectFit="contain"
                  data-testid="multi-slider-item-image"
                />
              </div>
            ))}
          </div>
        ) : (
          <Carousel
            carouselItems={list}
            itemComponent={Slide}
            variant="standard"
            idPrefix={`multi-slider`}
            classNameContainer={styles['multi-slider-carousel-container']}
            classNameIndicatorDots={styles['multi-slider-indicator-dots']}
            classNameItems={styles['multi-slider-wrapper-items']}
            classNameActiveDot={styles['multi-slider-active-dots']}
            withIndicators={config?.withIndicators}
            disableArrows={config?.disableArrows}
          />
        )}
      </Box>
    </Section>
  )
}

export { MultiSlider }
export default MultiSlider
